import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import { helpInfo, LeftHalfArrow, close, calendar } from "helper/icons";
import "react-datepicker/dist/react-datepicker.css";
import { TextInput } from "../textInput";
import moment from "moment";
import "./modules.css";

const CustomDateOfBirthPicker = (props) => {
  const {
    width = "w-[358px] lg:w-[384px] max-[400px]:w-[100%]",
    placeholder,
    dateFormat = "MM/DD/YYYY",
    showIconLeft = false,
    defaultDate = false,
    onSetDate,
    checkValidation = false,
    label,
    disabled = false,
    isFaded,
    maxDate,
    minDate,
    errorMsg,
    isNavigation = false,
  } = props || {};

  const [selectedDate, setSelectedDate] = useState(
    defaultDate ? moment.utc(defaultDate).toDate() : null
  );
  const [inputValue, setInputValue] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [view, setView] = useState("calendar");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const datePickerRef = useRef(null);
  const customHeaderRef = useRef(null);
  const portalRef = useRef(null);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (defaultDate) {
      const formattedDate = moment.utc(defaultDate).format(dateFormat);
      setInputValue(formattedDate);
    }
  }, [defaultDate, dateFormat]);
  
  useEffect(() => {
    if (!customHeaderRef.current) return;
    // Calendar is in a portal so query from document, not datePickerRef
    const listBoxElement = document.querySelector('[role="listbox"]');
    const dayNamesElement = customHeaderRef.current?.nextElementSibling;
    if (!listBoxElement || !dayNamesElement) return;

    if (view === "calendar") {
      listBoxElement.style.display = "";
      dayNamesElement.style.display = "";
    } else {
      listBoxElement.style.display = "none";
      dayNamesElement.style.display = "none";
    }
  }, [view]);

  useEffect(() => {
    function handleClickOutside(event) {
      const insideWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const insidePortal = portalRef.current && portalRef.current.contains(event.target);
      if (!insideWrapper && !insidePortal) {
        setShowDatePicker(false);
        setView("calendar");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setInputValue(date ? moment.utc(date).format(dateFormat) : "");
    if (onSetDate) onSetDate(moment.utc(date).format(dateFormat));
    setShowDatePicker(false);
    setView("calendar");
  };

  const toggleDatePicker = () => {
    if (!disabled) {
      if (!showDatePicker && wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setCalendarPos({
          top: rect.bottom + window.scrollY,
          left: rect.right - Math.max(rect.width, 230) + window.scrollX,
          width: rect.width,
        });
      }
      setShowDatePicker(!showDatePicker);
    }
  };

  const clearDate = () => {
    setSelectedDate(null);
    setInputValue("");
    if (onSetDate) onSetDate(null);
  };

  const openDate = moment.utc(defaultDate).format(dateFormat);

  return (
    <div className={`relative ${width} group`} ref={wrapperRef}>
      {!!label && (
        <span className="text-gray-700 font-medium text-sm">{label}</span>
      )}
      <div
        ref={datePickerRef}
        className={`custom-date-picker relative flex ${
          showIconLeft ? "flex-row-reverse" : "flex-row"
        }`}
      >
        <TextInput
          readOnly
          ref={inputRef}
          value={inputValue}
          onClick={toggleDatePicker}
          placeholder={placeholder}
          width={"w-full"}
          className={`${
            isFaded ? "bg-[#ffffff80]" : ""
          } ${showIconLeft ? "pl-9" : ""}`}
          errorMsg={errorMsg}
          disabled={disabled}
        />
          {!disabled && !selectedDate && (
            <img
              src={calendar}
              alt="Calendar"
              height={20}
              width={20}
              className={`custom-icon ${
                showIconLeft ? "custom-icon-left" : ""
              } ${
                !defaultDate && checkValidation ? "custom-icon-error" : ""
              } cursor-pointer absolute top-[22px] right-2`}
              onClick={toggleDatePicker}
            />
          )}
          {selectedDate && !disabled && (
            <img
              src={close}
              alt="Clear"
              height={20}
              width={20}
              className={`custom-icon ${
                showIconLeft ? "custom-icon-left" : ""
              } ${
                !defaultDate && checkValidation ? "custom-icon-error" : ""
              } cursor-pointer absolute top-[22px] right-2`}
              onClick={clearDate}
            />
          )}
           {isNavigation && (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <img src={helpInfo} alt="Help" className="cursor-pointer absolute top-[14px] right-9" />
              {isHovered && (
                <div className="absolute -bottom-32 right-0 w-64 p-3 bg-white shadow-100 border border-gray-300 rounded-lg z-[99999]">
                  <h3 className="text-sm font-semibold text-gray-800">📅 How to Use</h3>
                  <ul className="list-disc pl-4 text-xs text-gray-700">
                    <li>Click the calendar icon to open.</li>
                    <li>Click on the Year to choose a year.</li>
                    <li>Use arrows to navigate months.</li>
                    <li>Click on the X to reset.</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        {showDatePicker && ReactDOM.createPortal(
          <div
            ref={portalRef}
            style={{
              position: "absolute",
              top: calendarPos.top,
              left: calendarPos.left,
              zIndex: 99999,
            }}
          >
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            maxDate={maxDate ? new Date(maxDate) : null}
            minDate={minDate ? new Date(minDate) : null}
            onClickOutside={() => {
              setShowDatePicker(false);
              setView("calendar");
            }}
            inline
            openToDate={defaultDate ? new Date(openDate) : new Date()}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => {
              const currentYear = date.getFullYear();
              const startYear = currentYear - 6;
              const endYear = currentYear + 5;

              return (
                <div ref={customHeaderRef} className="custom-header">
                  {view === "calendar" ? (
                    <>
                      <button 
                        type="button" 
                        onClick={decreaseMonth} 
                        disabled={prevMonthButtonDisabled}
                      >
                        <LeftHalfArrow />
                      </button>
                      <button
                        type="button"
                        className="toggle-button text-gray-900 font-bold"
                        onClick={() => setView("years")}
                      >
                        {currentYear}
                      </button>
                      <button
                        type="button"
                        className="toggle-button text-gray-900 font-bold"
                        onClick={() => setView("months")}
                      >
                        {moment.months()[date.getMonth()]}
                      </button>
                      <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                        <LeftHalfArrow style={{ transform: "rotate(180deg)" }} />
                      </button>
                    </>
                  ) : view === "years" ? (
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <button
                          type="button"
                          className="arrow-button"
                          onClick={() => changeYear(startYear - 12)}
                        >
                          <LeftHalfArrow />
                        </button>
                        <button
                          type="button"
                          className="arrow-button"
                          onClick={() => changeYear(endYear + 1)}
                        >
                          <LeftHalfArrow style={{ transform: "rotate(180deg)" }} />
                        </button>
                      </div>
                      <div className="year-selector">
                        {Array.from({ length: 12 }, (_, i) => startYear + i).map((year) => (
                          <div
                            key={year}
                            className="year-item"
                            onClick={() => {
                              changeYear(year);
                              setView("calendar");
                            }}
                          >
                            {year}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="month-selector">
                      {moment.months().map((month, index) => (
                        <div
                          key={month}
                          className="month-item"
                          onClick={() => {
                            changeMonth(index);
                            setView("calendar");
                          }}
                        >
                          {month}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }}
          />
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default CustomDateOfBirthPicker;
